import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
} from '@angular/core';
import { IPermissions } from '@models/Configuration/IPermissions';
import { Comment } from '@models/Sessions/Idea/Comment';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { linkify } from '@utils/linkify';
import _ from 'lodash';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'comment',
  templateUrl: './comment.component.html',
  styleUrls: ['./comment.component.scss'],
})
export class CommentComponent implements OnChanges {
  @ViewChild('holder', { static: true }) holder: ElementRef;

  @Input() session: Session;
  @Input() idea: Idea;
  @Input() comment: Comment;
  @Input() focusedComment: Comment = undefined;
  @Input() preview = false;

  @Output() focusRequest: EventEmitter<ElementRef> =
    new EventEmitter<ElementRef>();
  @Output() reply: EventEmitter<Comment> = new EventEmitter<Comment>();

  segments: any;
  permissions: IPermissions;

  constructor(
    private appConfig: ConfigService,
    private bus: BusService,
    private events: EventsService,
  ) {
    this.permissions = appConfig.permissions;
  }

  ngOnChanges() {
    if (this.focused) {
      this.focusRequest.emit(this.holder);
    }

    this.segments = this.commentSegments(this.comment);
  }

  get focused(): boolean {
    return (
      this.comment &&
      this.focusedComment &&
      this.focusedComment._id.toString() === this.comment._id.toString()
    );
  }

  get participantDict() {
    return _.keyBy(this.session.config.participants, '_id');
  }

  get commentDate(): string {
    const date = new Date(this.comment.created_at);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  }

  commentSegments(comment) {
    const match = /(@[\w|(|)]+)|(\n) ?/g;
    const text = this.preview
      ? comment.text.substr(0, 128)
      : (comment.text ?? '');
    return text
      .split(match)
      .map((__: any) => {
        if (!__) {
          return null;
        }
        if (__ === '\n') {
          return { isNewline: true };
        }

        let _mention;
        if (
          comment.mentions &&
          (_mention = comment.mentions.find(mention => mention.text === __))
        ) {
          return {
            text: __,
            isMention: true,
            user: {
              name: __,
              _id: _mention.user_id,
            },
          };
        } else {
          return {
            text: __,
            html: linkify(__),
          };
        }
      })
      .filter((__: any) => __)
      .concat({
        text: this.preview && comment.text.length > text.length ? ' ...' : '',
      });
  }

  get replies() {
    return this.idea.comments.filter(
      comment => comment.parent_id === this.comment._id,
    );
  }

  deleteComment() {
    this.bus.publish(
      this.events.requested.action.session.idea.deleteComment,
      this.comment,
      this.idea,
      this.session,
    );
  }

  hideOneLevel() {
    if (!this.comment.hideReplies && this.replies.length > 0) {
      this.comment.hideReplies = true;
    } else {
      this.comment.hide = true;
      this.segments = this.commentSegments(this.comment);
    }
  }

  showOneLevel() {
    if (this.comment.hide) {
      this.comment.hide = false;
      this.segments = this.commentSegments(this.comment);
    } else {
      this.comment.hideReplies = false;
    }
  }
}
